import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Calculator, 
  MessageSquare, 
  Shield, 
  Users,
  CheckCircle,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { ROUTES } from '@/shared/config/routes.config';
import { useAuth } from '@/features/auth/hooks/useAuth';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Calculator,
      title: 'Benefits Calculator',
      description: 'Calculate your eligibility for UK benefits with our comprehensive tool',
      color: 'text-primary-600'
    },
    {
      icon: MessageSquare,
      title: 'AI Assistant',
      description: 'Get personalized guidance from our intelligent benefits advisor',
      color: 'text-secondary-600'
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Your data is encrypted and never shared with third parties',
      color: 'text-accent-600'
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Access to benefits specialists when you need help',
      color: 'text-primary-600'
    }
  ];

  const benefits = [
    'Universal Credit',
    'Housing Benefit',
    'Council Tax Reduction',
    'Child Benefit',
    'Personal Independence Payment',
    'Employment Support Allowance'
  ];

  const stats = [
    { value: '£2.5k', label: 'Average annual savings' },
    { value: '50k+', label: 'Users helped' },
    { value: '98%', label: 'Satisfaction rate' },
    { value: '24/7', label: 'AI assistance' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-secondary-50 opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-bold text-neutral-900 mb-6">
              Unlock Your Benefits
              <span className="block text-primary-600 mt-2">Made Simple</span>
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto mb-10">
              Discover and calculate your eligibility for UK government benefits with our intelligent platform. 
              Get personalized guidance and maximize your entitlements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <>
                  <Link to={ROUTES.CALCULATOR.INDEX}>
                    <Button variant="primary" size="lg" className="w-full sm:w-auto">
                      Open Calculator
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to={ROUTES.DASHBOARD}>
                    <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                      Go to Dashboard
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to={ROUTES.CALCULATOR.INDEX}>
                    <Button variant="primary" size="lg" className="w-full sm:w-auto">
                      Try Calculator
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to={ROUTES.AUTH.REGISTER}>
                    <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                      Create Free Account
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-neutral-900">{stat.value}</div>
                <div className="text-sm text-neutral-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Everything You Need to Navigate Benefits
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Our platform combines cutting-edge technology with expert knowledge to simplify your benefits journey
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow">
                <feature.icon className={`h-12 w-12 ${feature.color} mx-auto mb-4`} />
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-neutral-600">
                  {feature.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits List Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-6">
                Calculate Your Eligibility for All Major UK Benefits
              </h2>
              <p className="text-lg text-neutral-600 mb-8">
                Our comprehensive calculator covers all major benefit programs, ensuring you don't miss out on any support you're entitled to.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary-600 flex-shrink-0" />
                    <span className="text-neutral-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <Link to={ROUTES.BENEFITS} className="inline-block mt-8">
                <Button variant="outline">
                  View All Benefits
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-primary-100 to-secondary-100 rounded-2xl flex items-center justify-center">
                <TrendingUp className="h-32 w-32 text-primary-600" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Discover Your Benefits?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands who have already maximized their benefits with Civista
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ROUTES.AUTH.REGISTER}>
              <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to={ROUTES.CALCULATOR.INDEX}>
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-primary-600"
              >
                Try Calculator First
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;